import {
  AppointmentPreferences,
  PracticeTherapyDepartment,
} from "@physiofit-digital/physiofit_types";
import { StateCreator } from "zustand";
import { State } from "./mainStore";

export type AppointmentState = {
  appointmentHasRecipe: boolean | undefined;
  appointmentActionType:
    | "booking"
    | "cancellation"
    | "rescheduling"
    | undefined;
  appointmentRecipeAssets:
    | {
        assetUrl: string;
        file?: File;
        type: "image";
        internalName: string;
        mimeType?: string;
      }[]
    | undefined;
  appointmentTimeSlots: string[] | undefined;
  appointmentPatientNote: string | undefined;
  appointmentFirstName: string | undefined;
  appointmentLastName: string | undefined;
  appointmentBirthday: string | undefined;
  appointmentEmail: string | undefined;
  appointmentNationalPhone: string | undefined;
  appointmentCountryCode: string | undefined;
  appointmentRecipeUploadType: "camera" | "upload" | undefined;
  appointmentCancellationInfos: {
    appointmentDateTime: string | undefined;
  };
  appointmentReschedulingInfos: {
    appointmentDateTime: string | undefined;
  };
  rebookingWithoutRecipe: boolean | undefined;
  appointmentPreferences: AppointmentPreferences | undefined;
  appointmentCustomPreferenceIds: string[];
  appointmentPatientIsSelfPayer: boolean | undefined;
  appointmentTherapyDepartment: PracticeTherapyDepartment | undefined;
  appointmentSelfPayerDurationMinutes: number | undefined;
  appointmentSelfPayerAdditionalTreatments: string[] | undefined;

  setAppointmentFirstName: (firstName: string | undefined) => void;
  setAppointmentLastName: (lastName: string | undefined) => void;
  setAppointmentEmail: (email: string | undefined) => void;
  setAppointmentNationalPhone: (nationalPhone: string | undefined) => void;
  setAppointmentCountryCode: (countryCode: string | undefined) => void;
  setAppointmentBirthday: (birthday: string | undefined) => void;
  setAppointmentRecipeAssets: (
    recipeAssets:
      | {
          assetUrl: string;
          file?: File;
          type: "image";
          internalName: string;
        }[]
      | undefined,
  ) => void;
  setAppointmentHasRecipe: (hasRecipe: boolean | undefined) => void;
  setAppointmentTimeSlots: (timeSlots: string[] | undefined) => void;
  setAppointmentPatientNote: (timeSlots: string | undefined) => void;
  setAppointmentToModifyDateTime: (time: string | undefined) => void;
  setAppointmentRecipeUploadType: (
    uploadType: "camera" | "upload" | undefined,
  ) => void;
  setAppointmentPatientIsSelfPayer: (isSelfPayer: boolean | undefined) => void;
  setAppointmentActionType: (
    type: "booking" | "cancellation" | "rescheduling" | undefined,
  ) => void;
  setRebookingWithoutRecipe: (withoutRecipe: boolean | undefined) => void;
  setAppointmentPreferences: (
    preferences: AppointmentPreferences | undefined,
  ) => void;
  setAppointmentCustomPreferenceIds: (optionIds: string[]) => void;
  setAppointmentTherapyDepartment: (
    department: PracticeTherapyDepartment | undefined,
  ) => void;
  setAppointmentSelfPayerDurationMinutes: (minutes: number | undefined) => void;
  setAppointmentSelfPayerAdditionalTreatments: (treatments: string[] | undefined) => void;
  resetAppointmentState: (options?: { keepTherapyDepartment?: boolean }) => void;
};

const initialState: Omit<
  AppointmentState,
  keyof {
    [K in keyof AppointmentState as K extends `set${string}` | `reset${string}`
      ? K
      : never]: AppointmentState[K];
  }
> = {
  appointmentHasRecipe: undefined,
  appointmentActionType: undefined,
  appointmentRecipeAssets: undefined,
  appointmentTimeSlots: undefined,
  appointmentFirstName: undefined,
  appointmentLastName: undefined,
  appointmentEmail: undefined,
  appointmentNationalPhone: undefined,
  appointmentCountryCode: "+49",
  appointmentRecipeUploadType: undefined,
  appointmentPatientNote: undefined,
  appointmentCancellationInfos: {
    appointmentDateTime: undefined,
  },
  appointmentReschedulingInfos: {
    appointmentDateTime: undefined,
  },
  appointmentBirthday: undefined,
  rebookingWithoutRecipe: undefined,
  appointmentPreferences: undefined,
  appointmentCustomPreferenceIds: [],
  appointmentPatientIsSelfPayer: undefined,
  appointmentTherapyDepartment: undefined,
  appointmentSelfPayerDurationMinutes: undefined,
  appointmentSelfPayerAdditionalTreatments: undefined,
};

export const createAppointmentSlice: StateCreator<AppointmentState> = (
  set,
) => ({
  ...initialState,

  setAppointmentPatientNote: (timeSlots: string | undefined) =>
    set(() => ({
      appointmentPatientNote: timeSlots,
    })),
  setAppointmentRecipeUploadType: (
    uploadType: "camera" | "upload" | undefined,
  ) =>
    set(() => ({
      appointmentRecipeUploadType: uploadType,
    })),
  setAppointmentFirstName: (firstName: string | undefined) =>
    set(() => ({
      appointmentFirstName: firstName,
    })),
  setAppointmentLastName: (lastName: string | undefined) =>
    set(() => ({
      appointmentLastName: lastName,
    })),
  setAppointmentEmail: (email: string | undefined) =>
    set(() => ({
      appointmentEmail: email,
    })),
  setAppointmentNationalPhone: (nationalPhone: string | undefined) =>
    set(() => ({
      appointmentNationalPhone: nationalPhone,
    })),
  setAppointmentCountryCode: (countryCode: string | undefined) =>
    set(() => ({
      appointmentCountryCode: countryCode,
    })),
  setAppointmentBirthday: (birthday: string | undefined) =>
    set(() => ({
      appointmentBirthday: birthday,
    })),
  setAppointmentPatientIsSelfPayer: (isSelfPayer: boolean | undefined) =>
    set(() => ({
      appointmentPatientIsSelfPayer: isSelfPayer,
    })),
  setAppointmentRecipeAssets: (
    images:
      | {
          assetUrl: string;
          file?: File;
          type: "image";
          internalName: string;
        }[]
      | undefined,
  ) =>
    set(() => ({
      appointmentRecipeAssets: images,
    })),
  setAppointmentHasRecipe: (hasRecipe: boolean | undefined) =>
    set({ appointmentHasRecipe: hasRecipe }),
  setAppointmentTimeSlots: (timeSlots: string[] | undefined) =>
    set({ appointmentTimeSlots: timeSlots }),
  setAppointmentActionType: (
    type: "booking" | "cancellation" | "rescheduling" | undefined,
  ) => set({ appointmentActionType: type }),
  setAppointmentToModifyDateTime: (dateTime: string | undefined) =>
    set((state) => {
      const actionType = state.appointmentActionType;
      if (actionType === "cancellation") {
        return {
          appointmentCancellationInfos: {
            appointmentDateTime: dateTime,
          },
        };
      } else if (actionType === "rescheduling") {
        return {
          appointmentReschedulingInfos: {
            appointmentDateTime: dateTime,
          },
        };
      }
      return {};
    }),
  setRebookingWithoutRecipe: (withoutRecipe: boolean | undefined) =>
    set(() => ({
      rebookingWithoutRecipe: withoutRecipe,
    })),
  setAppointmentPreferences: (
    preferences: AppointmentPreferences | undefined,
  ) =>
    set(() => ({
      appointmentPreferences: preferences,
    })),
  setAppointmentCustomPreferenceIds: (optionIds: string[]) =>
    set(() => ({ appointmentCustomPreferenceIds: optionIds })),
  setAppointmentTherapyDepartment: (
    department: PracticeTherapyDepartment | undefined,
  ) =>
    set(() => ({
      appointmentTherapyDepartment: department,
    })),
  setAppointmentSelfPayerDurationMinutes: (minutes: number | undefined) =>
    set(() => ({ appointmentSelfPayerDurationMinutes: minutes })),
  setAppointmentSelfPayerAdditionalTreatments: (treatments: string[] | undefined) =>
    set(() => ({ appointmentSelfPayerAdditionalTreatments: treatments })),
  // The type screen resets on mount but sits behind the department screen, so it keeps that one value.
  resetAppointmentState: (options?: { keepTherapyDepartment?: boolean }) =>
    set((state) => ({
      ...initialState,
      ...(options?.keepTherapyDepartment
        ? { appointmentTherapyDepartment: state.appointmentTherapyDepartment }
        : {}),
    })),
});

export const getAppointmentTherapyDepartment = (state: AppointmentState) =>
  state.appointmentTherapyDepartment;

export const getAppointmentPracticeId = (state: State) =>
  state.currentPracticeId;
export const getAppointmentChainId = (state: State) => state.chainId;
export const getAppointmentHasRecipe = (state: AppointmentState) =>
  state.appointmentHasRecipe;
export const getAppointmentRecipeAssets = (state: AppointmentState) =>
  state.appointmentRecipeAssets;
export const getAppointmentTimeSlots = (state: AppointmentState) =>
  state.appointmentTimeSlots;
export const getAppointmentFirstName = (state: AppointmentState) =>
  state.appointmentFirstName;
export const getAppointmentLastName = (state: AppointmentState) =>
  state.appointmentLastName;
export const getAppointmentEmail = (state: AppointmentState) =>
  state.appointmentEmail;
export const getAppointmentNationalPhone = (state: AppointmentState) =>
  state.appointmentNationalPhone;
export const getAppointmentCountryCode = (state: AppointmentState) =>
  state.appointmentCountryCode;
export const getAppointmentRecipeUploadType = (state: AppointmentState) =>
  state.appointmentRecipeUploadType;
export const getAppointmentData = (state: State) => {
  const actionType = state.appointmentActionType;
  return {
    practiceId: state.currentPracticeId,
    chainId: state.chainId,
    timeSlots: state.appointmentTimeSlots,
    firstName: state.appointmentFirstName,
    lastName: state.appointmentLastName,
    email: state.appointmentEmail,
    birthday: state.appointmentBirthday,
    nationalPhone: state.appointmentNationalPhone,
    countryCode: state.appointmentCountryCode,
    appointmentPatientNote: state.appointmentPatientNote,
    requestType: state.appointmentActionType,
    appointmentToModifyDateTime:
      actionType === "rescheduling"
        ? state.appointmentReschedulingInfos.appointmentDateTime
        : actionType === "cancellation"
          ? state.appointmentCancellationInfos.appointmentDateTime
          : undefined,
    appointmentPreferences: state.appointmentPreferences,
  };
};
export const getAppointmentPatientNote = (state: AppointmentState) =>
  state.appointmentPatientNote;

export const getAppointmentActionType = (state: AppointmentState) =>
  state.appointmentActionType;

export const getAppointmentToModifyDateTime = (state: AppointmentState) => {
  const actionType = state.appointmentActionType;
  if (actionType === "cancellation") {
    return state.appointmentCancellationInfos.appointmentDateTime;
  } else if (actionType === "rescheduling") {
    return state.appointmentReschedulingInfos.appointmentDateTime;
  }
  return undefined;
};

export const getAppointmentBirthday = (state: AppointmentState) =>
  state.appointmentBirthday;

export const getRebookingWithoutRecipe = (state: AppointmentState) =>
  state.rebookingWithoutRecipe;

export const getAppointmentPreferences = (state: AppointmentState) =>
  state.appointmentPreferences;
export const getAppointmentCustomPreferenceIds = (state: AppointmentState) =>
  state.appointmentCustomPreferenceIds;
export const getAppointmentPatientIsSelfPayer = (state: AppointmentState) =>
  state.appointmentPatientIsSelfPayer;

export const getAppointmentSelfPayerDurationMinutes = (state: AppointmentState) =>
  state.appointmentSelfPayerDurationMinutes;
export const getAppointmentSelfPayerAdditionalTreatments = (state: AppointmentState) =>
  state.appointmentSelfPayerAdditionalTreatments;
